import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { Package, Truck, Scale, Globe, Calculator, Users } from 'lucide-react';

const ShippingExplained = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-[#F5EFE6]" data-testid="shipping-explained-page">
      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-2xl font-playfair font-bold text-primary tracking-tight">
            Sharedequal
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/calendar" className="text-sm font-medium text-primary">
              Calendar
            </Link>
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg shadow-sm">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg shadow-sm">
                  Login
                </Button>
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-playfair font-bold text-gray-900 tracking-tight mb-4">
            Shipping Explained
          </h1>
          <p className="text-lg text-gray-600">
            Understanding how polo gear shipping works and why group buying saves you money.
          </p>
        </div>

        {/* Why Polo Gear Is Expensive to Ship */}
        <Card className="mb-8 border-stone-200">
          <CardContent className="p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Package className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-playfair font-semibold text-gray-900 mb-2">
                  Why Polo Gear Is Expensive to Ship
                </h2>
                <p className="text-gray-600">
                  Shipping polo gear is more complex than shipping everyday items. Costs can feel surprisingly high when ordering alone due to how couriers price parcels.
                </p>
              </div>
            </div>
            
            <ul className="space-y-3 text-gray-700 ml-16">
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span>Long items like mallets occupy large space despite being light</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span>Helmets and boots are smaller, but higher value can increase customs duties and taxes</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span>When shipped individually, costs add up quickly</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* How Couriers Calculate Prices */}
        <Card className="mb-8 border-stone-200">
          <CardContent className="p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Scale className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-playfair font-semibold text-gray-900 mb-2">
                  How Couriers Calculate Prices
                </h2>
                <p className="text-gray-600">
                  Couriers use <strong>volumetric weight</strong> — pricing based on space taken up in aircraft or vehicles, not just actual weight.
                </p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4 ml-16">
              <div className="bg-stone-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Mallets</h4>
                <p className="text-sm text-gray-600">Require oversized cartons due to length</p>
              </div>
              <div className="bg-stone-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Helmets</h4>
                <p className="text-sm text-gray-600">Compact but higher value affects duties</p>
              </div>
              <div className="bg-stone-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Boots</h4>
                <p className="text-sm text-gray-600">Fall between both categories</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Why Shipping Increases in Steps */}
        <Card className="mb-8 border-stone-200">
          <CardContent className="p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Truck className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-playfair font-semibold text-gray-900 mb-2">
                  Why Shipping Increases in Steps
                </h2>
                <p className="text-gray-600">
                  Shipping works in tiers. Adding items may push shipment into higher carton size or pricing bracket.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Other Factors */}
        <Card className="mb-8 border-stone-200">
          <CardContent className="p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Globe className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-playfair font-semibold text-gray-900 mb-2">
                  Other Factors Affecting Cost
                </h2>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-3 ml-16">
              {[
                "Country of origin and destination",
                "Courier used",
                "Packaging constraints",
                "Oversized handling",
                "Fuel surcharges",
                "Customs duties and GST"
              ].map((factor, i) => (
                <div key={i} className="flex items-center gap-2 text-gray-700">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  <span>{factor}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Sharedequal's Approach */}
        <Card className="mb-8 border-stone-200 bg-primary/5">
          <CardContent className="p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-primary rounded-lg">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-playfair font-semibold text-gray-900 mb-2">
                  Sharedequal's Approach
                </h2>
              </div>
            </div>
            
            <ul className="space-y-4 text-gray-700 ml-16">
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold text-lg">1.</span>
                <span>Brands calculate shipping using their own checkout systems (value tiers + oversized rules)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold text-lg">2.</span>
                <span>Sharedequal does not change how brands price shipping</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold text-lg">3.</span>
                <span>Once final shipping is confirmed, costs are allocated fairly based on space and handling impact</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold text-lg">4.</span>
                <span>Oversized items (e.g. mallets) share oversized-related costs</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold text-lg">5.</span>
                <span>Remaining shipping is distributed across standard items</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Savings Breakdown */}
        <Card className="mb-8 border-primary border-2">
          <CardContent className="p-8">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary rounded-lg">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-playfair font-semibold text-gray-900 mb-4">
                  Transparent Savings Breakdown
                </h2>
                <p className="text-gray-700 text-lg">
                  Participants receive a transparent breakdown showing <strong>estimated individual shipping</strong> versus <strong>consolidated shipping</strong>, so savings are clearly visible.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* GST Note */}
        <div className="bg-amber-50 border border-amber-200 p-6 rounded-lg mb-8">
          <p className="text-amber-800">
            <strong>Note:</strong> GST applicable to shipping services will be included in the remaining balance payable upon collection.
          </p>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link to="/calendar">
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg px-8 py-3 font-medium">
              View Available Drops
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ShippingExplained;
